import { useMutation } from "@apollo/client";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ImplementationStatus } from "generated-graphql/graphql";
import { useCallback, useState } from "react";

import { BULK_SET_CI_STATUS } from "../api";
import { useEpcraBulkActionContext } from "./bulkActionContext";

const REFETCH = ["CredentialImplementationsForStaff", "CredentialImplementationForStaff"] as const;

const STATUSES = [
  ImplementationStatus.NotStarted,
  ImplementationStatus.InProgress,
  ImplementationStatus.Blocked,
  ImplementationStatus.Complete,
  ImplementationStatus.NotApplicable,
];

export function BulkUpdateStatusDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, onSuccess, selectedRows } = useEpcraBulkActionContext();
  const [implementationStatus, setImplementationStatus] = useState<ImplementationStatus>(
    ImplementationStatus.InProgress
  );
  const [blockedReason, setBlockedReason] = useState("");

  const [mutate, { loading }] = useMutation(BULK_SET_CI_STATUS, {
    refetchQueries: [...REFETCH],
  });

  const handleClose = useCallback(() => {
    setBlockedReason("");
    setImplementationStatus(ImplementationStatus.InProgress);
    onClose();
  }, [onClose]);

  const saveBlocked =
    implementationStatus === ImplementationStatus.Blocked && !(blockedReason?.trim() ?? "");

  const handleSubmit = useCallback(async () => {
    if (saveBlocked) return;
    const ids = selectedRows.map((r) => r.id);
    try {
      const { data } = await mutate({
        variables: {
          ids,
          implementationStatus,
          blockedReason:
            implementationStatus === ImplementationStatus.Blocked ? blockedReason.trim() : null,
        },
      });
      alerts.success(
        `Updated status on ${data?.bulkSetCredentialImplementationStatus.updated ?? 0} record(s)`
      );
      handleClose();
      onSuccess();
    } catch (e) {
      alerts.error("Could not update status", e);
    }
  }, [
    alerts,
    blockedReason,
    handleClose,
    implementationStatus,
    mutate,
    onSuccess,
    saveBlocked,
    selectedRows,
  ]);

  const handleStatusChange = (e: SelectChangeEvent<ImplementationStatus>) => {
    const next = e.target.value as ImplementationStatus;
    setImplementationStatus(next);
    if (next !== ImplementationStatus.Blocked) {
      setBlockedReason("");
    }
  };

  const open = dialogState === "status";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={`Update implementation status (${selectedRows.length} selected)`}
      description="All selected records will be set to the status you choose."
      primaryActionButtonProps={{
        onClick: () => void handleSubmit(),
        disabled: loading || saveBlocked,
        saveText: "Save",
      }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="bulk-ci-status-label">Implementation status</InputLabel>
          <Select<ImplementationStatus>
            labelId="bulk-ci-status-label"
            label="Implementation status"
            value={implementationStatus}
            onChange={handleStatusChange}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {prettyPrintEnumValue(s, [], true)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {implementationStatus === ImplementationStatus.Blocked ? (
          <TextField
            label="Blocked reason"
            value={blockedReason}
            onChange={(e) => setBlockedReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
            helperText="Required when status is Blocked"
          />
        ) : null}
      </Stack>
    </Dialog>
  );
}
